import { useMemo, useState } from "react";
import { Box, Button, Stack, TextField, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MapIcon from "@mui/icons-material/MapOutlined";
import NewCompanyDialog from "./dialogs/NewCompanyDialog";
import PageLayout from "components/PageLayout";
import OutlinedCard from "components/OutlinedCard";
import PageTitle from "components/PageTitle";
import {
  CompanyCompetence,
  CompanyCompetenceValues,
  CompanyState,
  CompanyStateValues,
} from "models/Company";
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryState } from "nuqs";
import CompaniesTable from "./components/CompaniesTable";
import SearchField from "components/SearchField";
import { useDebounce } from "hooks/useDebounce";
import SelectButton from "components/SelectButton";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import CompaniesMap from "./components/CompaniesMap";
import MultiSelect from "components/MultiSelect";
import FilterMenu from "components/FilterMenu";
import LoadingDisplay from "components/LoadingDisplay";
import ViewCompanyDialog from "./dialogs/ViewCompanyDialog";
import CompaniesProvider from "./CompaniesContext";
import { useCompaniesWithStats } from "./hooks/useCompaniesWithStats";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

const CompaniesPage: React.FunctionComponent = () => {
  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const [statusFilter, setStatusFilter] = useQueryState(
    "status",
    parseAsStringLiteral(Object.keys(CompanyStateValues) as CompanyState[])
      .withOptions({ clearOnDefault: false })
      .withDefault("ACTIVE")
  );

  const [view, setView] = useQueryState<"LIST" | "MAP">(
    "view",
    parseAsStringLiteral(["LIST", "MAP"]).withOptions({ clearOnDefault: false }).withDefault("LIST")
  );

  const [competencesFilter, setCompetencesFilter] = useQueryState<CompanyCompetence[]>(
    "competences",
    parseAsArrayOf(
      parseAsStringLiteral(Object.keys(CompanyCompetenceValues) as CompanyCompetence[])
    )
      .withOptions({ clearOnDefault: false })
      .withDefault([])
  );

  const { companies, isLoading } = useCompaniesWithStats();

  console.log(companies);

  const [showAddNewCompany, setShowAddNewCompany] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [selectedCompanyId, setSelectedCompanyId] = useQueryState<string>(
    "companyId",
    parseAsString
  );
  const selectedCompany = useMemo(
    () => companies?.find((company) => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const filteredCompanies = useMemo(() => {
    const query = debouncedSearchQuery.toLowerCase();
    return (
      companies
        ?.filter(
          (company) =>
            company.ueCode.toLowerCase().includes(query) ||
            company.name.toLowerCase().includes(query) ||
            company.city?.toLowerCase().includes(query)
        )
        .filter((company) => {
          if (statusFilter && company.state !== statusFilter) {
            return false;
          }
          if (competencesFilter && competencesFilter.length > 0) {
            return competencesFilter.every((competence) =>
              company.competences.includes(competence)
            );
          }
          return true;
        }) || []
    );
  }, [companies, statusFilter, competencesFilter, debouncedSearchQuery]);

  return (
    <CompaniesProvider
      companies={companies}
      selectedCompany={selectedCompany}
      setSelectedCompanyId={setSelectedCompanyId}
    >
      <PageLayout maxWidth={false} sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <PageTitle>Underentreprenörer</PageTitle>

        <OutlinedCard
          sx={{
            p: 1,
            flexGrow: view === "MAP" ? 1 : 0, // Map should fill page
            display: "grid",
            gridTemplateRows: "auto 1fr",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <SearchField
              label="Sök företag"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <FilterMenu
              activeFiltersCount={
                (statusFilter !== "ACTIVE" ? 1 : 0) +
                (competencesFilter && competencesFilter.length > 0 ? 1 : 0)
              }
              onReset={() => {
                setStatusFilter("ACTIVE");
                setCompetencesFilter([]);
              }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Status"
                  select
                  variant="filled"
                  value={statusFilter ?? ""}
                  onChange={(e) => setStatusFilter(e.target.value as CompanyState)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="" sx={{ color: "text.disabled" }}>
                    Alla
                  </MenuItem>
                  <MenuItem value="ACTIVE">Aktiva</MenuItem>
                  <MenuItem value="NEW">Nya</MenuItem>
                  <MenuItem value="ARCHIVED">Arkiverade</MenuItem>
                  <MenuItem value="BLOCKED">Blockerade</MenuItem>
                </TextField>

                <MultiSelect
                  label="Kompetenser"
                  value={competencesFilter ?? []}
                  onChange={(newValue) => setCompetencesFilter(newValue ?? [])}
                  options={Object.entries(CompanyCompetenceValues).map(([key, competence]) => ({
                    id: key as CompanyCompetence,
                    name: competence,
                  }))}
                />
              </Stack>
            </FilterMenu>

            <SelectButton
              value={view}
              onChange={setView}
              sx={{ ml: "auto", my: 1 }}
              options={[
                { value: "LIST", label: "Lista", icon: <ListIcon /> },
                { value: "MAP", label: "Karta", icon: <MapIcon /> },
              ]}
            />

            <Button
              variant="contained"
              onClick={() => setShowAddNewCompany(true)}
              startIcon={<AddIcon />}
              color="success"
              hidden={!canEditCompanies}
              sx={{ my: 1 }}
            >
              Nytt företag
            </Button>
          </Stack>

          {view === "LIST" ? (
            isLoading ? (
              <LoadingDisplay />
            ) : (
              <CompaniesTable companies={filteredCompanies} />
            )
          ) : (
            <Box sx={{ height: "100%" }}>
              <CompaniesMap companies={filteredCompanies} />
            </Box>
          )}
        </OutlinedCard>

        {showAddNewCompany && <NewCompanyDialog onClose={() => setShowAddNewCompany(false)} />}

        {selectedCompany && (
          <ViewCompanyDialog company={selectedCompany} onClose={() => setSelectedCompanyId(null)} />
        )}
      </PageLayout>
    </CompaniesProvider>
  );
};

export default CompaniesPage;
